import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CustomForm from "../general/form/CustomForm";
import ModalConfirm from "../general/auxiliar/modal/ModalConfirm";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import {
    putClinicPassword,
    deleteClinicPassword,
    getClinicHasPassword,
} from "../../actions/clinics";
import { fetchData } from "../../auxiliar/auxFunctions";

const Login = () => {
    const modalId = "delete-clinic-profile-modal";
    const {
        register,
        handleSubmit,
        handleSubmitWithoutValidation,
        openModal,
        watch,
        errors,
        isSubmitting,
    } = useFormWithDisable(modalId);

    const dispatch = useDispatch();

    const { id: clinicId } = useSelector((state) => state.activeClinic);
    const [context, setContext] = useState({
        waiting: true,
        has_password: false,
    });

    const newPassword = watch("new_info_password");

    useEffect(() => {
        fetchData(dispatch, getClinicHasPassword, clinicId, setContext);
    }, []);

    const formConfig = {
        new_info_password: {
            condition: true,
            disabled: false,
            type: "password",
            label: "Nueva contraseña *",
            placeholder: "********",
            name: "new_info_password",
            info: "La contraseña debe tener al menos 8 caracteres",
            validators: {
                required: "La nueva contraseña es obligatoria",
                minLength: {
                    value: 8,
                    message: "La nueva contraseña debe tener al menos 8 caracteres",
                },
            },
        },
        new_info_password_rep: {
            condition: true,
            disabled: false,
            type: "password",
            label: "Repita la nueva contraseña *",
            placeholder: "********",
            name: "new_info_password_rep",
            validators: {
                required: "La confirmación de la nueva contraseña es obligatoria",
                minLength: {
                    value: 8,
                    message: "La nueva contraseña debe tener al menos 8 caracteres",
                },
                validate: (value) => value === newPassword || "Las contraseñas no son iguales",
            },
        },
    };

    return (
        <section>
            <h2 className="sr-only">
                Perfiles de acceso para clínicas de fisioterapia en MiFisio Agenda - Controla el
                acceso a la información de tu clínica
            </h2>

            <h3 className="text-primary text-2xl">Perfiles de Acceso - Clínica</h3>

            <div className="my-3">
                <p className="text-base lg:text-lg mb-3">
                    {context?.has_password
                        ? "Esta clínica ya tiene un perfil registrado. Puedes cambiar la contraseña o eliminarlo utilizando el siguiente formulario."
                        : "Esta clínica no tiene un perfil registrado. Para crearlo puedes utilizar el siguiente formulario."}
                </p>

                <form>
                    <CustomForm
                        formConfig={formConfig}
                        errors={errors}
                        register={register}
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5"
                    />

                    <div className="mt-5 flex space-x-2 sm:space-x-4">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSubmit(putClinicPassword, clinicId, setContext)}
                            disabled={isSubmitting}
                        >
                            Confirmar
                        </button>
                        {context?.has_password && (
                            <button
                                className="btn btn-error btn-sm"
                                onClick={openModal}
                                disabled={isSubmitting}
                            >
                                Eliminar
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div className="bg-base-200 shadow-xl w-full rounded-lg p-8 mt-8">
                <h3 className="text-secondary text-2xl">¿Cómo funcionan los perfiles de acceso?</h3>

                <p className="text-base lg:text-lg mt-3">
                    En MiFisio Agenda puedes asignar distintos perfiles de acceso según el rol de
                    cada persona en tu clínica:
                </p>

                <ul className="text-base lg:text-lg mb-3 px-3 list-disc list-inside">
                    <li>
                        El <strong>perfil de manager</strong> tiene acceso completo: puede ver y
                        editar toda la información de la cuenta, gestionar clínicas, consultar la
                        facturación y exportar datos.
                    </li>
                    <li>
                        El <strong>perfil de clínica</strong> tiene permisos restringidos: solo
                        puede gestionar la actividad de su propia clínica. No puede acceder a datos
                        del perfil manager, a otras clínicas, ni a la facturación general o a la
                        exportación de datos.
                    </li>
                </ul>

                <p className="text-base lg:text-lg my-3">
                    Puedes crear un perfil de clínica utilizando el formulario anterior. Si ya
                    existe un perfil activo, puedes cambiar su contraseña o eliminarlo. Eliminar el
                    perfil no borra datos de la clínica, pero impedirá iniciar sesión hasta que se
                    cree una nueva contraseña. Para iniciar sesión como clínica debes utilizar el
                    correo electrónico de la clínica y la contraseña que has creado.
                </p>

                <p className="text-base lg:text-lg my-3">
                    Próximamente incorporaremos nuevos tipos de perfil con permisos personalizados,
                    para adaptarnos mejor a las necesidades de cada equipo de trabajo.
                </p>
            </div>

            <ModalConfirm
                id={modalId}
                title="¿Estás seguro de que quieres eliminar el perfil de la clínica?"
                text="Una vez eliminado, no podrás iniciar sesión como clínica hasta que se genere una nueva contraseña. Sin embargo, toda la información permanecerá accesible desde tu perfil de manager."
                onConfirm={handleSubmitWithoutValidation(
                    deleteClinicPassword,
                    clinicId,
                    setContext,
                )}
            />
        </section>
    );
};

export default Login;
